import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Send, MessageSquare } from 'lucide-react';
import { ContactForm, ChatMessages } from '../components/BuyerChatParts';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const STORAGE_KEY = 'vwatch_buyer_chat';

const api = async (endpoint, opts = {}) => {
  const res = await fetch(`${BACKEND}${endpoint}`, { ...opts, headers: { 'Content-Type': 'application/json', ...opts.headers } });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

const BuyerChat = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState('form');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [firstMsg, setFirstMsg] = useState('');
  const [selectedWatch, setSelectedWatch] = useState(null);
  const [watches, setWatches] = useState([]);
  const [watchesLoading, setWatchesLoading] = useState(true);
  const [convId, setConvId] = useState(null);
  const [convName, setConvName] = useState('');
  const [convStatus, setConvStatus] = useState('open');
  const [interestCount, setInterestCount] = useState(0);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const endRef = useRef(null);

  // Load watches for selector
  useEffect(() => {
    api('/api/chat/watches/list')
      .then(setWatches)
      .catch(e => console.warn('Watch list load failed:', e.message))
      .finally(() => setWatchesLoading(false));
  }, []);

  // Restore existing conversation
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { convId: cid, name: n } = JSON.parse(saved);
        if (cid) { setConvId(cid); setConvName(n); setStep('chat'); }
      }
    } catch (e) { console.warn('Restore conv failed:', e.message); }
  }, []);

  // Pre-fill watch from URL
  useEffect(() => {
    const watchParam = searchParams.get('watch');
    if (!watchParam) return;
    api('/api/chat/watches/list').then(list => {
      const match = list.find(w => w.id === watchParam);
      if (match) { setSelectedWatch(match); setFirstMsg(`Hi, I'm interested in the ${match.name}. Is it still available?`); }
    }).catch(e => console.warn('Watch prefill failed:', e.message));
  }, [searchParams]);

  // Poll messages
  useEffect(() => {
    if (!convId) return;
    const load = () => api(`/api/chat/${convId}/messages`).then(setMessages).catch(e => console.warn('Messages poll failed:', e.message));
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [convId]);

  // Poll status + interest
  useEffect(() => {
    if (!convId) return;
    const load = async () => {
      try {
        const conv = await api(`/api/chat/${convId}`);
        setConvStatus(conv.status || 'open');
        if (conv.watch_ref?.name) {
          const interest = await api(`/api/watches/${encodeURIComponent(conv.watch_ref.name)}/interest`);
          setInterestCount(interest.interested || 0);
        }
      } catch (e) { console.warn('Status poll failed:', e.message); }
    };
    load();
    const interval = setInterval(load, 8000);
    return () => clearInterval(interval);
  }, [convId]);

  // Auto-scroll
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleStart = useCallback(async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !firstMsg.trim()) return;
    setStarting(true);
    try {
      const data = await api('/api/chat/start', {
        method: 'POST', body: JSON.stringify({ name: name.trim(), email: email.trim(), message: firstMsg.trim(), watch_ref: selectedWatch }),
      });
      setConvId(data.id); setConvName(name.trim());
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ convId: data.id, name: name.trim() }));
      setStep('chat');
    } catch (e) { console.error('Start chat failed:', e.message); }
    finally { setStarting(false); }
  }, [name, email, firstMsg, selectedWatch]);

  const handleSend = useCallback(async () => {
    const text = newMsg.trim();
    if (!text || !convId) return;
    setSending(true);
    try {
      await api(`/api/chat/${convId}/messages`, { method: 'POST', body: JSON.stringify({ content: text, sender_name: convName }) });
      setNewMsg('');
      setMessages(await api(`/api/chat/${convId}/messages`));
    } catch (e) { console.error('Send failed:', e.message); }
    finally { setSending(false); }
  }, [newMsg, convId, convName]);

  const handleNewChat = () => {
    localStorage.removeItem(STORAGE_KEY);
    setConvId(null); setMessages([]); setName(''); setEmail(''); setFirstMsg(''); setSelectedWatch(null); setStep('form');
  };

  return (
    <div className="min-h-screen bg-[#0e0e11] text-[#e5e5e5] flex flex-col">
      <header className="sticky top-0 z-50 border-b border-[#2b2c2f] bg-[#141519]/80 backdrop-blur">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <img src="/vwatch-logo.png" alt="vWatch" className="h-9" />
          {step === 'chat' && <Button variant="ghost" size="sm" onClick={handleNewChat} className="text-xs text-[#888]" data-testid="new-chat-btn">New Conversation</Button>}
        </div>
      </header>
      <main className="flex-1 flex items-start justify-center px-4 py-8">
        <div className="w-full max-w-2xl">
          {step === 'form' ? (
            <Card className="bg-[#141519] border-[#2b2c2f] p-6 space-y-5" data-testid="chat-start-form">
              <div className="text-center space-y-1">
                <MessageSquare className="h-8 w-8 text-[#D0B36B] mx-auto" />
                <h2 className="text-xl font-semibold">Contact Us</h2>
                <p className="text-sm text-[#888]">Have a question about a watch? We're here to help.</p>
              </div>
              <ContactForm name={name} email={email} firstMsg={firstMsg} selectedWatch={selectedWatch}
                onNameChange={setName} onEmailChange={setEmail} onMsgChange={setFirstMsg}
                onSelectWatch={(w) => { setSelectedWatch(w); if (!firstMsg) setFirstMsg(`Hi, I'm interested in the ${w.name}. Is it still available?`); }}
                onDeselectWatch={() => setSelectedWatch(null)} onSubmit={handleStart} starting={starting}
                watches={watches} watchesLoading={watchesLoading} />
            </Card>
          ) : (
            <Card className="bg-[#141519] border-[#2b2c2f] overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 140px)', minHeight: '400px' }} data-testid="buyer-chat-panel">
              <div className="px-4 py-3 border-b border-[#2b2c2f] bg-[#18181d]">
                <p className="text-sm font-medium">vWatch Support</p>
                <p className="text-[11px] text-[#888]">We typically reply within a few minutes</p>
              </div>
              <ChatMessages messages={messages} convStatus={convStatus} interestCount={interestCount} endRef={endRef} />
              <div className="p-3 border-t border-[#2b2c2f] bg-[#18181d]">
                <form className="flex gap-2" onSubmit={e => { e.preventDefault(); handleSend(); }}>
                  <Input value={newMsg} onChange={e => setNewMsg(e.target.value)} placeholder="Type a message..." className="flex-1 text-sm bg-[#1a1a1f] border-[#2b2c2f]" data-testid="buyer-msg-input" disabled={sending} />
                  <Button type="submit" size="icon" disabled={sending || !newMsg.trim()} className="bg-[#D0B36B] text-black hover:bg-[#b89a55] shrink-0" data-testid="buyer-send-btn"><Send className="h-4 w-4" /></Button>
                </form>
              </div>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default BuyerChat;
